import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../App';

/**
 * CategorySelect - A select component with ability to add new categories
 * 
 * @param {string} value - Current selected value
 * @param {function} onValueChange - Callback when value changes
 * @param {string[]} categories - List of available categories
 * @param {string} categoryType - Type of category (income, expense, cash_income, cash_outcome, debt)
 * @param {string} placeholder - Placeholder text
 * @param {string} token - Auth token for API calls
 * @param {function} onCategoryAdded - Callback when a new category is added (to refresh categories)
 * @param {string} testId - data-testid for the select trigger
 */
export default function CategorySelect({
  value,
  onValueChange,
  categories = [],
  categoryType,
  placeholder = "Select a category",
  token,
  onCategoryAdded,
  testId
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/categories`, {
        name: newCategory.trim(),
        type: categoryType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Category "${newCategory.trim()}" added successfully`);
      
      // Select the new category
      onValueChange(newCategory.trim());
      
      // Notify parent to refresh categories
      if (onCategoryAdded) {
        onCategoryAdded();
      }
      
      setNewCategory('');
      setShowAddDialog(false);
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger className="flex-1" data-testid={testId}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setShowAddDialog(true)}
          title="Add new category"
          data-testid={testId ? `${testId}-add` : 'add-category-btn'}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Enter category name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
              data-testid="new-category-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              This category will be available for all future entries of this type.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory} disabled={adding} data-testid="save-category-btn">
              {adding ? 'Adding...' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
